import i18next from "i18next";
import { initReactI18next } from "react-i18next";
import LanguageDetector from "i18next-browser-languagedetector";

import deCommon from "./locales/de/common.json";
import { api } from "./api";

// Only DE is bundled statically; EN and other languages come from DB
const resources = {
  de: {
    common: deCommon,
  },
  en: {
    common: {}, // Empty placeholder - will be loaded from DB
  },
};

i18next
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources,
    // We use namespaces, default is 'common'
    defaultNS: "common",
    ns: ["common"],
    fallbackLng: "de",
    detection: {
      // Only respect explicit choices (URL, stored preference).
      // Do NOT auto-switch to browser language on first visit -> default stays DE.
      order: ["querystring", "localStorage", "cookie"],
      caches: ["localStorage", "cookie"],
    },
    interpolation: {
      escapeValue: false,
    },
    returnObjects: true,
  });

export default i18next;

// Cache translations in localStorage for faster loads
const CACHE_VERSION = 'v1';
const CACHE_TTL_MS = 60 * 60 * 1000; // 1 hour

function getCachedTranslations(locale, ns) {
  try {
    const key = `i18n_cache_${CACHE_VERSION}_${locale}_${ns}`;
    const cached = localStorage.getItem(key);
    if (!cached) return null;
    const { data, timestamp } = JSON.parse(cached);
    if (Date.now() - timestamp > CACHE_TTL_MS) {
      localStorage.removeItem(key);
      return null;
    }
    return data;
  } catch {
    return null;
  }
}

function setCachedTranslations(locale, ns, data) {
  try {
    const key = `i18n_cache_${CACHE_VERSION}_${locale}_${ns}`;
    localStorage.setItem(key, JSON.stringify({ data, timestamp: Date.now() }));
  } catch {}
}

// Attempt to fetch server-side translated resources (MySQL translation memory)
async function loadServerResources(locale, ns = "common") {
  // Check localStorage cache first
  const cached = getCachedTranslations(locale, ns);
  if (cached) {
    console.log('[i18n] Using cached translations for', locale);
    const bundle = unflatten(cached);
    if (Object.keys(bundle).length) {
      i18next.addResourceBundle(locale, ns, bundle, true, true);
      return true;
    }
  }
  
  try {
    const base = (import.meta.env.VITE_API_BASE_URL || `${window.location.origin}/api`).replace(/\/$/, "");
    const url = `${base}/i18n/resources?locale=${encodeURIComponent(locale)}&ns=${encodeURIComponent(ns)}`;
    const resp = await fetch(url, { credentials: "include" });
    const data = await resp.json().catch(() => null);
    if (resp.ok && data && data[ns]) {
      // Cache the flat data before unflattening
      setCachedTranslations(locale, ns, data[ns]);
      
      // Server returns a flat map of keys (e.g., "login.welcome": "...")
      // i18next expects nested objects when using default keySeparator ('.').
      // Convert the flat map to nested shape before adding to the store.
      const bundle = unflatten(data[ns]);
      console.log('[i18n] Loaded server resources for', locale, ':', Object.keys(bundle).length, 'top-level keys');
      if (Object.keys(bundle).length) {
        // For locales like 'en', server translations should override the
        // placeholder JSON in the frontend bundle.
        i18next.addResourceBundle(locale, ns, bundle, true, true);
        console.log('[i18n] Added resource bundle for', locale);
        return true;
      }
    }
  } catch (e) {
    console.error('[i18n] Failed to load server resources:', e);
  }
  return false;
}

// On init and language change, ensure resources are present. If missing, batch translate from DE.
async function ensureLocaleResources(locale) {
  const ns = "common";
  let updated = await loadServerResources(locale, ns);
  
  // Skip batch-translate for DE (source language) or if we already have translations loaded
  if (locale === 'de') return updated;
  
  const existing = i18next.getResourceBundle(locale, ns);
  const dstKeys = existing && typeof existing === 'object' ? flatten(existing) : {};
  
  // Only process if we have very few translations (< 10% of expected)
  // This prevents unnecessary batch-translate calls on every page load
  if (Object.keys(dstKeys).length > 100) {
    console.log('[i18n] Skipping batch-translate - already have', Object.keys(dstKeys).length, 'translations');
    return updated;
  }
  
  // If locale is not 'de' and we have no keys, request batch translation from backend
  const srcKeys = deCommon && typeof deCommon === 'object' ? flatten(deCommon) : {};
  const missing = Object.entries(srcKeys)
    .filter(([k]) => !(k in dstKeys))
    .map(([k, v]) => ({ key: k, text: String(v) }));
  const needsUpdate = Object.entries(srcKeys)
    .filter(([k, v]) => (k in dstKeys) && String(dstKeys[k]) === String(v))
    .map(([k, v]) => ({ key: k, text: String(v) }));
  const toProcess = [...missing, ...needsUpdate];
  
  if (toProcess.length) {
    console.log('[i18n] Batch-translating', toProcess.length, 'keys for', locale);
    try {
      const base = (import.meta.env.VITE_API_BASE_URL || `${window.location.origin}/api`).replace(/\/$/, "");
      const resp = await fetch(`${base}/i18n/batch-translate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ from: 'de', to: locale, namespace: ns, entries: toProcess }),
      });
      const data = await resp.json().catch(() => null);
      if (resp.ok && data && data.translations) {
        i18next.addResourceBundle(locale, ns, unflatten(data.translations), true, true);
        // Update cache with new translations
        const currentCache = getCachedTranslations(locale, ns) || {};
        setCachedTranslations(locale, ns, { ...currentCache, ...data.translations });
        updated = true;
      }
    } catch (e) {
      console.error('[i18n] Batch-translate failed:', e);
    }
  }
  return updated;
}

function flatten(obj, prefix = '') {
  return Object.entries(obj).reduce((acc, [k, v]) => {
    const key = prefix ? `${prefix}.${k}` : k;
    if (typeof v === 'object' && v !== null) {
      Object.assign(acc, flatten(v, key));
    } else {
      acc[key] = v;
    }
    return acc;
  }, {});
}

function unflatten(dict) {
  const result = {};
  for (const [k, v] of Object.entries(dict)) {
    const parts = k.split('.');
    let node = result;
    for (let i = 0; i < parts.length; i++) {
      const p = parts[i];
      if (i === parts.length - 1) {
        // If node[p] is already an object with children, don't overwrite it
        if (typeof node[p] === 'object' && node[p] !== null && Object.keys(node[p]).length > 0) {
          console.warn(`[i18n] Skipping key "${k}" - parent already has children`);
        } else {
          node[p] = v;
        }
      } else {
        // If node[p] is a string, convert it to an object (children take precedence)
        if (typeof node[p] !== 'object' || node[p] === null) {
          node[p] = {};
        }
        node = node[p];
      }
    }
  }
  return result;
}

// Kick off loading for current language and, if new resources were added,
// trigger a no-op language change to force UI re-render.
(async () => {
  try {
    const updated = await ensureLocaleResources(i18next.language || 'de');
    if (updated) {
      await i18next.changeLanguage(i18next.language || 'de');
    }
  } catch {}
})();
i18next.on('languageChanged', (lng) => ensureLocaleResources(lng));

// Helper: preload resources, then switch language to ensure UI updates immediately
export async function changeLanguageSafe(lng) {
  console.log('[i18n] changeLanguageSafe called with:', lng);
  try {
    await ensureLocaleResources(lng);
  } catch (e) {
    console.error('[i18n] ensureLocaleResources failed:', e);
  }
  await i18next.changeLanguage(lng);
  console.log('[i18n] Language changed to:', lng);
}
